import { useCallback, useSyncExternalStore } from 'react';
import { safeStorage } from './utils';

/**
 * How much detail chat panes show. `simple` (default): clean chat, steps collapsed. `standard`: friendly steps
 * with per-step details and token totals. `advanced`: the terminal look. Per browser, like the theme.
 */
export type ChatView = 'simple' | 'standard' | 'advanced';
export const CHAT_VIEWS: readonly ChatView[] = ['simple', 'standard', 'advanced'];
export const CHAT_VIEW_KEY = 'af:chat-view';

export function readChatView(): ChatView {
  const v = safeStorage.get<string>(CHAT_VIEW_KEY);
  return v === 'standard' || v === 'advanced' ? v : 'simple';
}

const listeners = new Set<() => void>();

export function setChatView(v: ChatView) {
  if (v === 'simple') safeStorage.remove(CHAT_VIEW_KEY);
  else safeStorage.set(CHAT_VIEW_KEY, v);
  for (const l of listeners) l();
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export function useChatView(): [ChatView, (v: ChatView) => void] {
  const view = useSyncExternalStore(subscribe, readChatView, () => 'simple' as ChatView);
  return [view, useCallback((v: ChatView) => setChatView(v), [])];
}
